<template>
  <CLoading :loading="loading" />
  <div class="q-mb-md" style="margin-top: -60px">
    <TestModeSelector v-model="isTestMode" :isHideCopy="true" />
  </div>
  <q-table
    bordered
    flat
    dense
    :title="$t('label.traffic_source.traffic_sources_list')"
    ref="welcomeList"
    v-if="trafficSources"
    :rows="trafficSources"
    row-key="title"
    :rows-per-page-options="[10]"
    separator="cell"
    :no-data-label="$t('messages.no_data_available')"
    class="text-grey-8"
  >
    <template v-slot:header="props">
      <q-tr>
        <q-th rowspan="2" key="action" class="text-center" style="width: 100px"></q-th>
        <q-th :props="props" rowspan="2" key="source_name" style="width: 50px">
          <span class="text-bold">{{ $t('label.traffic_source.source_name') }}</span>
        </q-th>
        <q-th :props="props" rowspan="2" key="num_of_traffic" style="width: 50px">
          <span class="text-bold">{{ $t('label.traffic_source.num_of_traffic') }}</span>
        </q-th>
        <q-th :props="props" rowspan="2" key="num_of_users">
          <span class="text-bold">{{ $t('label.traffic_source.num_of_users') }}</span>
        </q-th>
        <q-th :props="props" rowspan="2" key="qr_code" style="width: 100px">
          <span class="text-bold">{{ $t('label.traffic_source.qr_code') }}</span>
        </q-th>
        <q-th :props="props" rowspan="2" key="tracking_url">
          <span class="text-bold">{{ $t('label.traffic_source.tracking_url') }}</span>
        </q-th>
      </q-tr>
    </template>
    <template v-slot:body="props">
      <q-tr :props="props" :index="props.rowKey">
        <q-td key="action" :props="props.index">
          <q-btn size="sm" flat round icon="edit" @click="onEdit(props.row)" class="q-mr-sm" />
          <app-copy-button @click="onCopy(props.row)" class="q-mr-sm" />
          <q-btn size="sm" flat round color="red" icon="delete" @click="onDelete(props.row)" class="q-mr-sm" />
        </q-td>
        <q-td key="source_name" :props="props">
          {{ props.row.source_name }}
        </q-td>
        <q-td key="num_of_traffic" :props="props">
          {{ props.row.num_of_traffic }}
        </q-td>
        <q-td key="num_of_users" :props="props">
          {{ props.row.num_of_users }}
        </q-td>
        <q-td key="qr_code" :props="props">
          <q-btn icon="qr_code_2" @click="onQRCode(props.row)" />
        </q-td>
        <q-td key="tracking_url" :props="props" class="tdbreak">
          <span class="hyper-link">
            <TagContent :tagValue="getTrackingURL(props.row)"></TagContent>
          </span>
        </q-td>
      </q-tr>
    </template>

    <template v-slot:top>
      <h2 class="text-h6 text-black">{{ $t('label.traffic_source.traffic_sources_list') }}</h2>
      <q-select
        outlined
        dense
        option-value="_id"
        option-label="campaign_name"
        v-model="selectedCampaignId"
        :options="campaigns"
        emit-value
        map-options
        class="q-ma-md"
        style="width: 240px"
        :label="$t('label.popup.campaign')"
      />
      <q-space />
      <DateRangeSelectorV2
        v-model="dateRange"
        :disable="false"
        @update:onChangeDateRange="changeDateRange"
      ></DateRangeSelectorV2>
      <q-btn class="q-ml-md btn-save" no-caps @click="onAdd(0)" :label="$t('add_new')" />
    </template>
  </q-table>

  <TrafficSourceFormModal
    v-if="modalVisible"
    :modalVisible="modalVisible"
    :source="selectedTrafficSource"
    :campaignId="selectedCampaignId"
    @update:closeModal="onCloseModal"
  />

  <QRCodeFormModal
    v-if="modalQRCodeVisible"
    :modalVisible="modalQRCodeVisible"
    :source="selectedTrafficSource"
    @update:closeModal="onCloseModal"
  />
</template>

<script lang="ts">
import { Options, mixins } from 'vue-class-component'
import { maska } from 'maska'
import { IApp, ICampaign, IDateRangePicker, ITrafficSource } from '@/utils/types'
import { Watch } from 'vue-property-decorator'
import cloneDeep from 'lodash/cloneDeep'
import { ACTION_APP, ACTION_CAMPAIGN, ACTION_TRAFFIC_SOURCE } from '@/store/actions'
import CLoading from '@/components/common/ui/CLoading.vue'
import TrafficSourceFormModal from '@/components/traffic-sources/TrafficSourceFormModal.vue'
import QRCodeFormModal from '@/components/traffic-sources/QRCodeFormModal.vue'
import TagContent from '@/components/common/ui/TagContent.vue'
import BaseFormMixin from '../common/mixins/BaseFormMixin.vue'
import { DatetimeUtils } from '@/utils/datetime'
import DateRangeSelectorV2 from '@/components/common/ui/DateRangeSelectorV2.vue'
import TestModeSelector from '@/components/common/ui/TestModeSelector.vue'

@Options({
  components: {
    CLoading,
    TrafficSourceFormModal,
    QRCodeFormModal,
    TagContent,
    DateRangeSelectorV2,
    TestModeSelector,
  },
  directives: { maska },
  emits: ['update:changeTab'],
})
export default class TrafficSourceList extends mixins(BaseFormMixin) {
  loading = true
  modalVisible = false
  modalQRCodeVisible = false
  selectedTrafficSource: ITrafficSource = {}
  selectingApp: IApp = {}
  selectedCampaignId = ''
  campaigns: ICampaign[] = []
  isTestMode = false
  firstLoad = false

  dateRange: IDateRangePicker = {
    startDate: '',
    endDate: '',
  }

  get selectedAppId() {
    return this.$route.params.app_id as string
  }

  get trafficSources() {
    const result = cloneDeep(this.$store.getters.trafficSources) || []
    if (result.length > 0) {
      for (const element of result) {
        element.tracking_url = this.getTrackingURL(element)
      }
    }

    return result
  }

  get campaignId() {
    return this.$route.params.campaign_id as string || ''
  }

  get getIsTestMode() {
    return this.$route.params.is_test_mode === 'true' || false
  }

  @Watch('isTestMode')
  async isTestModeChange() {
    if (this.firstLoad) {
      return
    }

    this.loading = true
    this.firstLoad = true
    await this.loadData()
    if (this.campaigns.length > 0) {
      this.selectedCampaignId = this.campaigns[0]._id ?? ''
    }

    await this.loadTrafficSource()
    this.firstLoad = false
    this.loading = false
  }

  @Watch('campaignId', { immediate: true })
  async appIdChanged() {
    if (!this.selectedAppId) {
      return
    }
    this.firstLoad = true

    this.dateRange = DatetimeUtils.getDateRange('last30Days')

    this.selectingApp = await this.$store.dispatch(ACTION_APP.LOAD_ITEM, this.selectedAppId)
    this.isTestMode = this.getIsTestMode
    await this.loadData()
    this.selectedCampaignId = this.campaignId
    await this.loadTrafficSource()

    this.firstLoad = false
    this.loading = false
  }

  @Watch('selectedCampaignId')
  handleChangeCampaign() {
    if (this.firstLoad) {
      return
    }
    this.loadTrafficSource()
  }

  changeDateRange(value) {
    if (this.firstLoad) {
      return
    }

    this.dateRange = value
    this.loadTrafficSource()
  }

  async loadData() {
    const cams = await this.$store.dispatch(ACTION_CAMPAIGN.LOAD_COMPACT_ITEMS, {
      app_id: this.selectedAppId,
      is_traffic_source: true,
      is_test_mode: this.isTestMode,
    })
    this.campaigns = cams
  }

  async loadTrafficSource() {
    await this.$store.dispatch(ACTION_TRAFFIC_SOURCE.LOAD_ITEMS, {
      app_id: this.selectedAppId,
      campaign_id: this.selectedCampaignId,
      is_test_mode: this.isTestMode,
      date_from: DatetimeUtils.formatDate(this.dateRange.startDate),
      date_to: DatetimeUtils.formatDate(this.dateRange.endDate),
    })
  }

  onAdd() {
    this.selectedTrafficSource = {
      app_id: this.selectedAppId,
      is_active: !this.isTestMode,
      is_test_mode: this.isTestMode,
    }

    this.modalVisible = true
  }

  onCopy(data: ITrafficSource) {
    const targetCopy: ITrafficSource = cloneDeep(data)
    targetCopy._id = ''
    targetCopy.is_active = !this.isTestMode
    targetCopy.is_test_mode = this.isTestMode
    targetCopy.source_name = targetCopy.source_name + ' Copy'
    this.selectedTrafficSource = targetCopy
    this.modalVisible = true
  }

  onEdit(data: ITrafficSource) {
    this.selectedTrafficSource = data
    this.modalVisible = true
  }

  async onDelete(data: ITrafficSource) {
    this.$q
      .dialog({
        title: this.$t('confirm'),
        message: this.$t('are_you_sure_you_want_to_delete'),
        cancel: true,
        persistent: true,
      })
      .onOk(async () => {
        const success = await this.$store.dispatch(ACTION_TRAFFIC_SOURCE.DELETE, data._id)
        if (success) {
          this.$q.notify({
            message: this.$t('messages.deleted'),
            color: 'positive',
          })
        }
      })
  }

  onCloseModal() {
    this.modalVisible = false
    this.modalQRCodeVisible = false
    this.selectedTrafficSource = {}
  }

  getTrackingURL(data: ITrafficSource) {
    let accountId = this.selectingApp.account_id
    let liffId = this.selectingApp.liff_id
    if (this.isTestMode) {
      accountId = this.selectingApp.account_id_test
      liffId = this.selectingApp.liff_id_test
    }

    const params = encodeURIComponent(
      `account_id=${accountId}&liff_id=${liffId}&is_test_mode=${this.isTestMode}&source_id=${data._id}&campaign_id=${
        data.campaign_id ?? ''
      }`
    )
    const url = `https://liff.line.me/${liffId}?${params}`
    return url
  }

  onQRCode(data: ITrafficSource) {
    this.selectedTrafficSource = data
    this.modalQRCodeVisible = true
  }

  goto(name: string, params = {}) {
    this.$router.push({
      name,
      params,
    })
  }
}
</script>
<style lang="scss" scoped>
.tdbreak {
  word-break: break-all;
  white-space: normal;
}
</style>
